import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

const TrendBadge = ({ trendDays }: { trendDays: number | null }) => {
  if (trendDays === null) return null

  const isImproved = trendDays < 0
  const label = isImproved
    ? `↓ ${Math.abs(trendDays).toFixed(1)}d faster`
    : `↑ ${trendDays.toFixed(1)}d slower`

  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        isImproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {label} vs prev period
    </span>
  )
}

export const TimeToHirePage = () => {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedJobId, setSelectedJobId] = useState<string>('all')

  const fromAt = fromDate ? new Date(fromDate).getTime() : undefined
  const toAt = toDate ? new Date(toDate).getTime() : undefined

  const { data: report, isLoading } = useQuery(
    convexQuery(api.features.candidates.time_to_hire.fetchMyTimeToHireReport, {
      fromAt,
      toAt,
    }),
  )

  const jobOptions = report?.byJob ?? []
  const filteredJobs =
    selectedJobId === 'all'
      ? jobOptions
      : jobOptions.filter((j) => j.jobId === selectedJobId)

  return (
    <div className="flex h-full w-full flex-col px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-8">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="date"
            className="w-40"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="From"
          />
          <Input
            type="date"
            className="w-40"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="To"
          />
          {jobOptions.length > 1 && (
            <Select
              value={selectedJobId}
              onValueChange={(v) => setSelectedJobId(v ?? 'all')}
            >
              <SelectTrigger className="w-56">
                <SelectValue placeholder="All jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All jobs</SelectItem>
                {jobOptions.map((j) => (
                  <SelectItem key={j.jobId} value={j.jobId}>
                    {j.jobTitle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {isLoading ? null : (
          <>
            {selectedJobId === 'all' && report && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex flex-col gap-1 rounded-md border border-border bg-card p-5">
                  <p className="text-xs font-medium uppercase tracking-wide text-primary/50">
                    Total hired
                  </p>
                  <p className="text-3xl font-bold text-primary">
                    {report.totalHired}
                  </p>
                </div>

                <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-5">
                  <p className="text-xs font-medium uppercase tracking-wide text-primary/50">
                    Avg. time to hire
                  </p>
                  <p className="text-3xl font-bold text-primary">
                    {report.averageDays !== null
                      ? `${report.averageDays.toFixed(1)}d`
                      : '—'}
                  </p>
                  <TrendBadge trendDays={report.trendDays} />
                </div>

                <div className="flex flex-col gap-1 rounded-md border border-border bg-card p-5">
                  <p className="text-xs font-medium uppercase tracking-wide text-primary/50">
                    Median time to hire
                  </p>
                  <p className="text-3xl font-bold text-primary">
                    {report.medianDays !== null
                      ? `${report.medianDays.toFixed(1)}d`
                      : '—'}
                  </p>
                </div>
              </div>
            )}

            <section className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold text-primary">
                Per-job breakdown
              </h2>
              {filteredJobs.length === 0 ? (
                <p className="text-sm text-primary/60">
                  No hired candidates found in this period.
                </p>
              ) : (
                <div className="overflow-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-secondary text-left">
                        <th className="px-4 py-3 font-medium text-primary/70">
                          Job
                        </th>
                        <th className="px-4 py-3 font-medium text-primary/70">
                          Hired
                        </th>
                        <th className="px-4 py-3 font-medium text-primary/70">
                          Avg. days
                        </th>
                        <th className="px-4 py-3 font-medium text-primary/70">
                          Median days
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobs.map((jobStat) => (
                        <tr
                          key={jobStat.jobId}
                          className="border-b border-border last:border-0 hover:bg-secondary/50"
                        >
                          <td className="px-4 py-3 font-medium text-primary">
                            {jobStat.jobTitle}
                          </td>
                          <td className="px-4 py-3 text-primary/80">
                            {jobStat.hiredCount}
                          </td>
                          <td className="px-4 py-3 text-primary/80">
                            {jobStat.averageDays !== null
                              ? `${jobStat.averageDays.toFixed(1)}d`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-primary/80">
                            {jobStat.medianDays !== null
                              ? `${jobStat.medianDays.toFixed(1)}d`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
